<template>
  <div class="event-halted-overlay" :class="`event-halted-overlay--${ended ? 'ended' : 'paused'}`">
    <div class="event-halted-overlay__panel">
      <span class="event-halted-overlay__icon">
        <i :class="ended ? 'pi pi-stop-circle' : 'pi pi-pause'" />
      </span>

      <span class="event-halted-overlay__title">{{ ended ? 'Event ended' : 'Event paused' }}</span>

      <span v-if="ended" class="event-halted-overlay__text">
        This sale is closed and every asset's outcome is final. No further clerk actions can be taken on this event.
      </span>
      <span v-else class="event-halted-overlay__text">
        Bidding is halted for every asset. Buyers can't place bids and no clerk actions are accepted until you resume.
        The event picks up exactly where it left off.
      </span>

      <div class="event-halted-overlay__actions">
        <Button v-if="ended" class="event-halted-overlay__leave" @click="emit('leave')">
          <i class="pi pi-arrow-left" />Back
        </Button>
        <Button v-else class="event-halted-overlay__resume" :disabled="resuming" @click="emit('resume')">
          <i class="pi pi-play" />{{ resuming ? 'Resuming…' : 'Resume Event' }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import Button from 'primevue/button';

// Covers the whole stage while the sale is not running, so no clerk action can
// be taken on a lot the engine is not listening about.
//
// The two states are not variants of each other. PAUSED is recoverable and
// amber, and the way out is Resume. FINISHED is terminal and red, and there is
// no way out except leaving — which is why the only control is Back, and why
// this called `window.history.back()` directly. It emits `leave` now; where
// "back" goes is the application's routing, not this overlay's.
//
// It sits absolute against the stage rather than the viewport, deliberately:
// the top bar stays reachable so the clerk can still see which sale this is and
// get at settings.
defineOptions({ name: 'EventHaltedOverlay' });

const props = defineProps({
  // PAUSED | FINISHED — the runtime's event status.
  status: { type: String, required: true, validator: (value) => ['PAUSED', 'FINISHED'].includes(value) },
  // A resume is in flight.
  resuming: { type: Boolean, default: false },
});

const emit = defineEmits(['resume', 'leave']);

const ended = computed(() => props.status === 'FINISHED');
</script>

<style scoped>
.event-halted-overlay {
  position: absolute;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(3px);
  background: var(--p-event-halted-overlay-background);
  padding: var(--p-event-halted-overlay-padding);
}

.event-halted-overlay__panel {
  max-width: 560px;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: var(--p-event-halted-overlay-panel-background);
  border: var(--p-event-halted-overlay-panel-border-width) solid var(--p-event-halted-overlay-panel-border-color);
  border-radius: var(--p-event-halted-overlay-panel-border-radius);
  padding: var(--p-event-halted-overlay-panel-padding);
  gap: var(--p-event-halted-overlay-panel-gap);
}

.event-halted-overlay--ended .event-halted-overlay__panel {
  border-color: var(--p-event-halted-overlay-ended-panel-border-color);
}

.event-halted-overlay__icon {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--p-event-halted-overlay-icon-border-radius);
  background: var(--p-event-halted-overlay-icon-background);
  border: var(--p-event-halted-overlay-icon-border-width) solid var(--p-event-halted-overlay-icon-border-color);
  color: var(--p-event-halted-overlay-icon-color);
  font-size: var(--p-event-halted-overlay-icon-font-size);
}

.event-halted-overlay--ended .event-halted-overlay__icon {
  background: var(--p-event-halted-overlay-ended-icon-background);
  border-color: var(--p-event-halted-overlay-ended-icon-border-color);
  color: var(--p-event-halted-overlay-ended-icon-color);
}

.event-halted-overlay__title {
  font-size: var(--p-event-halted-overlay-title-font-size);
  font-weight: var(--p-event-halted-overlay-title-font-weight);
  color: var(--p-event-halted-overlay-title-color);
}

.event-halted-overlay__text {
  font-size: var(--p-event-halted-overlay-text-font-size);
  line-height: var(--p-event-halted-overlay-text-line-height);
  color: var(--p-event-halted-overlay-text-color);
}

.event-halted-overlay__actions {
  display: flex;
  justify-content: flex-end;
}

/* Neutral, not tone-filled: leaving a finished sale is not a destructive act,
   and dressing the only way out in red reads as a warning about pressing it. */
.p-button.event-halted-overlay__leave {
  display: inline-flex;
  align-items: center;
  font-family: inherit;
  padding: var(--p-event-halted-overlay-action-padding);
  border-radius: var(--p-event-halted-overlay-action-border-radius);
  font-size: var(--p-event-halted-overlay-action-font-size);
  font-weight: var(--p-event-halted-overlay-action-font-weight);
  gap: var(--p-event-halted-overlay-action-gap);
  background: var(--p-event-halted-overlay-leave-background);
  border: var(--p-event-halted-overlay-leave-border-width) solid var(--p-event-halted-overlay-leave-border-color);
  color: var(--p-event-halted-overlay-leave-color);
}

.p-button.event-halted-overlay__resume {
  display: inline-flex;
  align-items: center;
  font-family: inherit;
  border: none;
  padding: var(--p-event-halted-overlay-action-padding);
  border-radius: var(--p-event-halted-overlay-action-border-radius);
  font-size: var(--p-event-halted-overlay-action-font-size);
  font-weight: var(--p-event-halted-overlay-action-font-weight);
  gap: var(--p-event-halted-overlay-action-gap);
  background: var(--p-event-halted-overlay-resume-background);
  color: var(--p-event-halted-overlay-resume-color);
}

.p-button.event-halted-overlay__resume:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}
</style>
